import { useState, useEffect, useRef } from "react";

/* ── THEME ─────────────────────────────────────────────────────────────────── */
const T = {
  bg: "#f8fafc", card: "#ffffff", border: "#e2e8f0",
  text: "#0f172a", muted: "#475569", dim: "#94a3b8",
  blue: { bg: "#dbeafe", text: "#1e40af", border: "#93c5fd" },
  green: { bg: "#dcfce7", text: "#166534", border: "#86efac" },
  red: { bg: "#fee2e2", text: "#991b1b", border: "#fca5a5" },
  amber: { bg: "#fef3c7", text: "#92400e", border: "#fcd34d" },
  purple: { bg: "#ede9fe", text: "#5b21b6", border: "#c4b5fd" },
  teal: { bg: "#ccfbf1", text: "#0e7490", border: "#5eead4" },
  orange: { bg: "#ffedd5", text: "#9a3412", border: "#fdba74" },
};

/* ── SHARED COMPONENTS ──────────────────────────────────────────────────────── */
const Tag = ({ color = T.blue, children }) => (
  <span style={{ display:"inline-flex", alignItems:"center", padding:"2px 10px", borderRadius:20,
    background: color.bg, color: color.text, fontSize:11, fontWeight:700,
    letterSpacing:"0.05em", textTransform:"uppercase", border:`1px solid ${color.border}` }}>
    {children}
  </span>
);
const Card = ({ children, style = {} }) => (
  <div style={{ background: T.card, border:`1px solid ${T.border}`, borderRadius:12,
    padding:"20px 24px", marginBottom:16, boxShadow:"0 1px 3px rgba(0,0,0,0.04)", ...style }}>
    {children}
  </div>
);
const SectionLabel = ({ color = T.blue, children }) => (
  <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
    <div style={{ width:4, height:28, borderRadius:2, background: color.text }} />
    <span style={{ fontFamily:"Georgia,serif", fontSize:13, fontWeight:700,
      color: color.text, letterSpacing:"0.08em", textTransform:"uppercase" }}>
      {children}
    </span>
  </div>
);
const HR = () => <div style={{ height:1, background: T.border, margin:"32px 0" }} />;
function ScoreRing({ score, total, color = T.blue }) {
  const pct = total ? score / total : 0;
  const r = 28, circ = 2 * Math.PI * r;
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
      <svg width={70} height={70}>
        <circle cx={35} cy={35} r={r} fill="none" stroke={T.border} strokeWidth={6} />
        <circle cx={35} cy={35} r={r} fill="none" stroke={color.text} strokeWidth={6}
          strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)}
          strokeLinecap="round" transform="rotate(-90 35 35)"
          style={{ transition:"stroke-dashoffset 0.6s ease" }} />
        <text x={35} y={40} textAnchor="middle" fontSize={15} fontWeight={700} fill={T.text}>
          {score}/{total}
        </text>
      </svg>
      <span style={{ fontSize:11, color: T.muted, fontWeight:600 }}>{total ? Math.round(pct*100) : 0}%</span>
    </div>
  );
}
function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold:0.05 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}
function Reveal({ children, delay = 0 }) {
  const [ref, visible] = useReveal();
  return (
    <div ref={ref} style={{ opacity:visible?1:0, transform:visible?"none":"translateY(18px)",
      transition:`opacity 0.55s ease ${delay}ms, transform 0.55s ease ${delay}ms` }}>
      {children}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   SECTION 1 – UMIR & ABUSIVE TRADING
══════════════════════════════════════════════════════════════════════════════ */
function Section1() {
  const [active, setActive] = useState(null);
  const violations = [
    { id:0, icon:"🏆", label:"Best Execution", tag:"UMIR Core Duty", color:T.blue,
      simple:"Get your client the BEST POSSIBLE OUTCOME — not just the fastest trade, not just the cheapest commission. It's the TOTAL result.",
      factors:[
        { f:"Price", d:"Most important factor. Can you beat the National Best Bid/Offer (NBBO)?" },
        { f:"Speed", d:"How fast? Critical for day traders in fast markets." },
        { f:"Certainty", d:"Will the trade actually get filled? Key for illiquid stocks." },
        { f:"Total Cost", d:"Commission PLUS bid-ask spread — both count." },
      ],
      trap:"ILLIQUID STOCK TRAP: Placing one market order for 100k shares of a stock with 5k daily volume CRASHES the price with the client's own order. Use limit orders and 'work the order' over days — prioritize PRICE over SPEED.",
    },
    { id:1, icon:"🧼", label:"Wash Trading", tag:"Abusive — Fake Volume", color:T.red,
      simple:"Trading with YOURSELF to create fake volume. No real change in ownership. Pure deception to lure in other investors.",
      factors:[
        { f:"Account A (RR's)", d:"Sells 10,000 shares of SpecuStock at $5.00" },
        { f:"Account B (Wife's)", d:"Simultaneously buys 10,000 shares at $5.00" },
        { f:"Result", d:"Market sees '10,000 share block' — but it's a lie. Net ownership: unchanged." },
        { f:"Intent", d:"Create fake excitement so the RR can dump REAL shares on 'suckers' who herd in." },
      ],
      trap:"The ONLY purpose is deception. No real economic purpose exists. CIRO enforcement = massive fines + permanent ban.",
    },
    { id:2, icon:"👻", label:"Spoofing / Layering", tag:"Abusive — Fake Orders", color:T.red,
      simple:"Placing a FAKE order you NEVER intend to fill — just to scare the market into moving in your favour.",
      factors:[
        { f:"Step 1", d:"Place massive fake 'BUY' order at $50.00 (the 'spoof')" },
        { f:"Step 2", d:"Other investors see 'wall of support' and buy at $50.01, $50.02..." },
        { f:"Step 3", d:"Cancel the fake $50 order the moment price moves up" },
        { f:"Step 4", d:"Sell your REAL shares at the new higher $50.02 price" },
      ],
      trap:"The spoof order was never 'bona fide' (genuine). It was a deceptive tool. High-speed traders love this — CIRO hunts it via algorithmic surveillance.",
    },
    { id:3, icon:"🎭", label:"Artificial Pricing ('Marking the Close')", tag:"Abusive — Fake Price", color:T.amber,
      simple:"Making a trade NOT as a real investment, but to MANIPULATE the closing price for personal gain.",
      factors:[
        { f:"Who does this?", d:"Portfolio Managers whose BONUS is tied to year-end fund performance" },
        { f:"The Action", d:"At 3:59 PM Dec 31, place massive market buy order for top holding" },
        { f:"The Fake Result", d:"Stock price 'spikes' at close → fund value is marked UP → bigger bonus" },
        { f:"The Crime", d:"Intent was NOT a 'good investment' — it was deception to manufacture performance" },
      ],
      trap:"Any trade placed with the INTENT to manipulate a price — not to make a genuine investment — is Artificial Pricing regardless of how legitimate it appears.",
    },
    { id:4, icon:"🏃", label:"Front Running", tag:"Client Priority Violation", color:T.purple,
      simple:"Using PRIVATE knowledge of a client's pending order to trade for YOURSELF FIRST — before or during their execution.",
      factors:[
        { f:"10:00 AM", d:"Mutual fund client orders 5 MILLION shares of Suncor. You overhear this." },
        { f:"10:01 AM", d:"You log into YOUR personal account and buy 1,000 Suncor shares" },
        { f:"10:02 AM", d:"The 5M-share order executes, pushing Suncor up 2% (from $50 to $51)" },
        { f:"10:03 AM", d:"You sell your 1,000 shares for instant risk-free $1,000 profit" },
      ],
      trap:"Your 1,000-share buy may have COST the client more for their own trade. This is criminal-level. Immediate firing + permanent industry ban. Also applies to SPOUSE'S account and any account you control.",
    },
  ];
  return (
    <Reveal>
      <SectionLabel color={T.blue}>8.1 — UMIR & Abusive Trading</SectionLabel>
      <Card style={{ background:"linear-gradient(135deg,#eff6ff,#fdf4ff)", border:`1px solid ${T.blue.border}` }}>
        <p style={{ margin:0, fontSize:14, color:T.text, lineHeight:1.7, fontStyle:"italic" }}>
          <strong>Analogy — The Public Auction House.</strong> All the KYC/Suitability rules govern your relationship with clients.
          UMIR governs your relationship with <em>the market itself</em>. Violating these is not bad service — it's market manipulation or fraud.
          Consequences: massive fines, suspensions, permanent bans, criminal charges.
        </p>
      </Card>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(260px,1fr))", gap:10, marginBottom:16 }}>
        {violations.map(v => (
          <button key={v.id} onClick={() => setActive(active===v.id ? null : v.id)}
            style={{ padding:"14px 16px", borderRadius:10, border:`2px solid ${active===v.id ? v.color.text : T.border}`,
              background:active===v.id ? v.color.bg : T.card, cursor:"pointer", textAlign:"left", transition:"all 0.2s" }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
              <span style={{ fontSize:22 }}>{v.icon}</span>
              <div>
                <div style={{ fontWeight:700, fontSize:13, color:T.text }}>{v.label}</div>
                <Tag color={v.color}>{v.tag}</Tag>
              </div>
            </div>
          </button>
        ))}
      </div>
      {active !== null && (() => { const v = violations[active]; return (
        <Card style={{ border:`1.5px solid ${v.color.border}`, background:v.color.bg }}>
          <p style={{ margin:"0 0 12px", fontSize:13, color:v.color.text, fontWeight:700, lineHeight:1.6 }}>📌 {v.simple}</p>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(220px,1fr))", gap:8, marginBottom:12 }}>
            {v.factors.map((f,i) => (
              <div key={i} style={{ background:T.card, borderRadius:8, padding:"10px 12px", border:`1px solid ${T.border}` }}>
                <div style={{ fontSize:11, fontWeight:700, color:v.color.text, marginBottom:3 }}>{f.f}</div>
                <div style={{ fontSize:12, color:T.muted, lineHeight:1.5 }}>{f.d}</div>
              </div>
            ))}
          </div>
          <div style={{ background:T.card, borderRadius:8, padding:"10px 14px", border:`1px solid ${T.amber.border}` }}>
            <span style={{ fontSize:11, fontWeight:700, color:T.amber.text }}>⚡ Exam Trap: </span>
            <span style={{ fontSize:12, color:T.text }}>{v.trap}</span>
          </div>
        </Card>
      );})()}
    </Reveal>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   SECTION 2 – GATEKEEPING & SUSPICIOUS TRANSACTIONS
══════════════════════════════════════════════════════════════════════════════ */
function Section2() {
  const [drillStep, setDrillStep] = useState(0);
  const redFlags = [
    { icon:"💵", label:"Inconsistent with Financials", example:"$250k cash deposit → KYC says $40k annual income", suspect:"Money Laundering" },
    { icon:"🎓", label:"Inconsistent with Knowledge", example:"'Novice' investor suddenly requesting complex options + short-selling strategies", suspect:"Nominee / 'Mule' Account" },
    { icon:"📊", label:"Inconsistent with Risk Profile", example:"'Low Risk' retiree wants 100% of RRSP in one penny stock", suspect:"Pump & Dump scheme participation" },
    { icon:"🔄", label:"Inconsistent with Past Activity", example:"Blue-chip mutual fund buyer for 20 years → suddenly 50 trades/day in illiquid penny stocks", suspect:"Manipulation or Insider Information" },
    { icon:"🤔", label:"Inconsistent with Logic", example:"Wants to buy AND sell same stock at same price simultaneously", suspect:"Wash Trading" },
    { icon:"📈", label:"Perfectly-Timed Pattern", example:"Buys out-of-money call options day BEFORE earnings 3 times in a row — each time 1,000%+ gain", suspect:"Insider Trading" },
  ];
  const drillSteps = [
    { step:1, icon:"🚩", title:"RECOGNIZE the Red Flag", color:T.red,
      script:"'This is weird. My 80-yr-old Low Risk client has never traded before, and he just gave me the symbol for a tiny mining company and wants to buy $100,000 RIGHT NOW.'" },
    { step:2, icon:"❓", title:"INQUIRE (Gently)", color:T.amber,
      script:"'Mr. Smith, this is certainly a different kind of investment for us. To make sure I'm doing my job, can you tell me how you heard about this company? What was your research process?'" },
    { step:3, icon:"⏸️", title:"PAUSE the Trade", color:T.purple,
      script:"'Mr. Smith, I CANNOT place this trade at this moment. The rules require me to understand the full context of a trade that is outside a client's normal profile. I must discuss this with my supervisor first.'" },
    { step:4, icon:"📢", title:"ESCALATE Immediately", color:T.blue,
      script:"Call Branch Manager: 'I am pausing a trade for client Smith. It is highly suspicious and inconsistent with his KYC. 100% into one penny stock, evasive about source. I need you to review BEFORE I accept the order.'" },
    { step:5, icon:"📝", title:"DOCUMENT Everything", color:T.green,
      script:"Write detailed note: what happened, what client said, what YOU said, who you escalated to, timestamp. This is your legal proof of fulfilling gatekeeper duty." },
  ];
  return (
    <Reveal delay={60}>
      <SectionLabel color={T.red}>8.2 — Gatekeeping & Suspicious Transactions</SectionLabel>
      <Card style={{ background:T.red.bg, border:`1px solid ${T.red.border}` }}>
        <strong style={{ color:T.red.text, fontSize:13 }}>🚪 The Nightclub Bouncer Analogy</strong>
        <p style={{ margin:"6px 0 0", fontSize:12, color:T.text, lineHeight:1.7 }}>
          Your job is NOT just to 'let people in' (accept orders). Your PRIMARY job is to <strong>protect the integrity of the market</strong>.
          You must stop guests trying to start a fight (manipulation), rob people (fraud), or use fake IDs (money laundering).
          <strong> "I was just following the client's orders" is NOT a defense. It is a violation.</strong>
        </p>
      </Card>
      <strong style={{ fontSize:13, color:T.text, display:"block", marginBottom:10 }}>🚩 The Red Flag Checklist — KYC is Your #1 Compliance Tool</strong>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px,1fr))", gap:10, marginBottom:20 }}>
        {redFlags.map((r,i) => (
          <div key={i} style={{ padding:"12px 14px", borderRadius:10, border:`1px solid ${T.border}`, background:T.card }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
              <span style={{ fontSize:20 }}>{r.icon}</span>
              <span style={{ fontWeight:700, fontSize:12, color:T.text }}>{r.label}</span>
            </div>
            <div style={{ fontSize:11, color:T.muted, fontStyle:"italic", marginBottom:6, lineHeight:1.5 }}>{r.example}</div>
            <Tag color={T.red}>Suspect: {r.suspect}</Tag>
          </div>
        ))}
      </div>
      <strong style={{ fontSize:13, color:T.text, display:"block", marginBottom:10 }}>🔥 The RR's Fire Drill — Mandatory Process</strong>
      <div style={{ display:"flex", gap:6, marginBottom:12, flexWrap:"wrap" }}>
        {drillSteps.map((s,i) => (
          <button key={i} onClick={() => setDrillStep(i)}
            style={{ padding:"8px 14px", borderRadius:8, border:`2px solid ${drillStep===i ? s.color.text : T.border}`,
              background:drillStep===i ? s.color.bg : T.card, cursor:"pointer", fontSize:12,
              fontWeight:700, color:drillStep===i ? s.color.text : T.muted, transition:"all 0.2s" }}>
            {s.icon} Step {s.step}
          </button>
        ))}
      </div>
      {(() => { const s = drillSteps[drillStep]; return (
        <Card style={{ border:`1.5px solid ${s.color.border}`, background:s.color.bg }}>
          <div style={{ fontWeight:700, fontSize:14, color:T.text, marginBottom:8 }}>{s.icon} {s.title}</div>
          <p style={{ margin:0, fontSize:13, color:T.text, fontStyle:"italic", lineHeight:1.7, background:T.card,
            padding:"12px 16px", borderRadius:8, border:`1px solid ${T.border}` }}>"{s.script}"</p>
        </Card>
      );})()}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginTop:4 }}>
        <Card style={{ border:`1px solid ${T.purple.border}`, background:T.purple.bg }}>
          <strong style={{ color:T.purple.text, fontSize:13 }}>🕵️ Insider Trading</strong>
          <p style={{ margin:"8px 0 0", fontSize:12, color:T.text, lineHeight:1.7 }}>
            Using <strong>Material, Non-Public Information (MNPI)</strong> to trade.
            <br/><strong>Material</strong> = would change the stock price (merger, failed drug trial, bad earnings)
            <br/><strong>Non-Public</strong> = the public doesn't know yet
            <br/><strong>Tipping</strong> = giving MNPI to a 'tippee' so they can trade — equally illegal
          </p>
        </Card>
        <Card style={{ border:`1px solid ${T.amber.border}`, background:T.amber.bg }}>
          <strong style={{ color:T.amber.text, fontSize:13 }}>📣 Whistleblower Program (OSC)</strong>
          {[
            ["Financial Award", "Up to $5 million if tip leads to successful enforcement"],
            ["Anonymity", "Can report anonymously (via a lawyer)"],
            ["Anti-Reprisal", "ILLEGAL for employer to fire/demote for reporting"],
            ["Tipping Off Prohibition", "NEVER tell client an STR was filed — criminal offense"],
          ].map(([k,v],i) => (
            <div key={i} style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginTop:6, gap:8 }}>
              <span style={{ fontWeight:700, color:T.amber.text, flexShrink:0 }}>{k}:</span>
              <span style={{ color:T.text, textAlign:"right" }}>{v}</span>
            </div>
          ))}
        </Card>
      </div>
    </Reveal>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   SECTION 3 – ORDER TYPES
══════════════════════════════════════════════════════════════════════════════ */
function Section3() {
  const [selected, setSelected] = useState(0);
  const orders = [
    { name:"Market Order", icon:"⚡", color:T.blue, tag:"Speed & Certainty",
      simple:"'Buy/Sell RIGHT NOW at the best available price.' — Like hitting 'Buy It Now' on eBay.",
      pro:"100% guaranteed to fill immediately", con:"ZERO price control — 'slippage' in volatile/illiquid markets",
      goodFor:"Highly liquid blue-chip stocks (RBC, Apple) with tiny bid-ask spreads (1 cent)",
      badFor:"Illiquid penny stocks — fills at ASK price, instantly locking in a 10%+ loss",
      mechanic:"Buy Market → fills at ASK price. Sell Market → fills at BID price." },
    { name:"Limit Order", icon:"🎯", color:T.green, tag:"Price Control",
      simple:"'I will ONLY trade at THIS price or better.' — Like making a Best Offer on eBay.",
      pro:"100% price control — guaranteed to fill at your price or better",
      con:"NO guarantee of execution — price may never reach your limit",
      goodFor:"Illiquid stocks, precise entries, clients with specific price requirements",
      badFor:"When speed is critical — 'I want this filled NOW regardless of price'",
      mechanic:"Buy Limit: set BELOW market (willing to pay $48, stock is $50). Sell Limit: set ABOVE market (will only sell at $52, stock is $50)." },
    { name:"Stop-Loss Order", icon:"🛑", color:T.red, tag:"Trigger → Market Order",
      simple:"NOT a Limit Order. A TRIGGER that launches a Market Order once price is touched.",
      pro:"Automates loss-cutting without monitoring the market all day",
      con:"FLASH CRASH DISASTER: If price touches $95 stop, becomes Market Order, fills at $75 during crash — then snaps back to $99",
      goodFor:"Speculative/High-Risk clients who understand the risk",
      badFor:"Low Risk clients — can guarantee selling at the exact worst price during a temporary crash",
      mechanic:"Set BELOW current price. When price TOUCHES the stop → becomes Market Order → fills at NEXT available price (which may be far lower)." },
    { name:"Stop-Buy Order", icon:"🚀", color:T.purple, tag:"Trigger → Market (Above Market)",
      simple:"Set ABOVE current price. Triggers a buy when price breaks through a resistance level.",
      pro:"Perfect for 'momentum' or 'breakout' technical traders",
      con:"As a Market Order trigger, can fill significantly above the stop price in fast markets",
      goodFor:"Speculative/Technical clients only. Also used to close a short position (Buy to Cover).",
      badFor:"Conservative clients — a highly speculative tool",
      mechanic:"Stock at $98, resistance at $100. Set Stop-Buy at $100.01. If price touches $100.01 → Market Buy triggers → fills at ~$100.10. Also use to 'Buy to Cover' a short with rising price." },
    { name:"IOC — Immediate or Cancel", icon:"🔬", color:T.teal, tag:"Duration: Partial Fill OK",
      simple:"'Get me WHATEVER you can RIGHT NOW, cancel the rest.' — Allows partial fills.",
      pro:"Reveals available liquidity without leaving an order 'live' in the market",
      con:"May result in a small partial fill — strategy may require full quantity",
      goodFor:"Large PM 'probing' orders — send 50k IOC, get 8k, cancel the 42k, repeat later",
      badFor:"Strategies that need the FULL quantity (use FOK instead)",
      mechanic:"Sweeps market instantly. Fills any available shares at your price. Immediately cancels the unfilled remainder." },
    { name:"FOK — Fill or Kill", icon:"💣", color:T.orange, tag:"Duration: All or Nothing",
      simple:"'Fill the ENTIRE order IMMEDIATELY, or cancel the WHOLE thing. No partial fills.'",
      pro:"Ensures complete strategy execution — critical for arbitrage",
      con:"May result in zero fill if full quantity unavailable",
      goodFor:"Arbitrage traders who must buy 10k of Stock A AND short 5k of Stock B simultaneously — partial fill ruins the strategy",
      badFor:"When partial fill is acceptable (use IOC instead)",
      mechanic:"Unlike IOC, FOK requires 100% of the order to fill INSTANTLY. If only 2,500 of 10,000 shares are available — entire order is cancelled." },
    { name:"Iceberg Order", icon:"🧊", color:T.blue, tag:"Hidden Institutional Tool",
      simple:"Show only the 'tip' — hide the full order size to prevent price crash.",
      pro:"Institutional Best Execution tool — avoids crashing own position's price",
      con:"Slower to fill as market only sees small display size",
      goodFor:"Large fund managers selling 1M+ share blocks. Standard tool in institutional trading.",
      badFor:"N/A — not typically used by retail clients",
      mechanic:"Total order: 1,000,000 shares. Display size: 1,000 shares. Market sees 1k sold → refills from hidden order → repeats 1,000 times. Other traders never see the full size." },
  ];
  const o = orders[selected];
  return (
    <Reveal delay={80}>
      <SectionLabel color={T.purple}>8.3 — Order Types</SectionLabel>
      <Card style={{ background:T.amber.bg, border:`1px solid ${T.amber.border}` }}>
        <p style={{ margin:0, fontSize:13, color:T.text, lineHeight:1.7 }}>
          <strong>The order type IS part of your recommendation.</strong> Using a Market Order on an illiquid penny stock is a
          Best Execution violation. Using a Stop-Loss for a Low Risk client can be catastrophically unsuitable.
          Know which tool to use for which situation.
        </p>
      </Card>
      <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:16 }}>
        {orders.map((o,i) => (
          <button key={i} onClick={() => setSelected(i)}
            style={{ padding:"8px 14px", borderRadius:8,
              border:`2px solid ${selected===i ? o.color.text : T.border}`,
              background:selected===i ? o.color.bg : T.card, cursor:"pointer",
              fontSize:12, fontWeight:700, color:selected===i ? o.color.text : T.muted,
              display:"flex", alignItems:"center", gap:5, transition:"all 0.2s" }}>
            <span>{o.icon}</span><span>{o.name}</span>
          </button>
        ))}
      </div>
      <Card style={{ border:`1.5px solid ${o.color.border}`, background:o.color.bg }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
          <span style={{ fontSize:28 }}>{o.icon}</span>
          <div>
            <div style={{ fontFamily:"Georgia,serif", fontWeight:700, fontSize:16, color:T.text }}>{o.name}</div>
            <Tag color={o.color}>{o.tag}</Tag>
          </div>
        </div>
        <p style={{ margin:"0 0 12px", fontSize:13, color:T.text, lineHeight:1.7, fontStyle:"italic" }}>{o.simple}</p>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(200px,1fr))", gap:8 }}>
          {[
            ["✓ Advantage", o.pro, T.green],
            ["✗ Risk / Trap", o.con, T.red],
            ["✅ Good For", o.goodFor, T.teal],
            ["⚠️ Unsuitable For", o.badFor, T.amber],
          ].map(([label,text,color],i) => (
            <div key={i} style={{ background:T.card, borderRadius:8, padding:"10px 12px", border:`1px solid ${color.border}` }}>
              <div style={{ fontSize:11, fontWeight:700, color:color.text, marginBottom:4 }}>{label}</div>
              <div style={{ fontSize:12, color:T.muted, lineHeight:1.5 }}>{text}</div>
            </div>
          ))}
        </div>
        <div style={{ marginTop:12, padding:"10px 14px", borderRadius:8, background:T.card, border:`1px solid ${T.border}` }}>
          <span style={{ fontSize:11, fontWeight:700, color:T.blue.text }}>⚙️ Mechanic: </span>
          <span style={{ fontSize:12, color:T.text }}>{o.mechanic}</span>
        </div>
      </Card>
    </Reveal>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   SECTION 4 – SETTLEMENT & TRADE EXECUTION
══════════════════════════════════════════════════════════════════════════════ */
function Section4() {
  const [errView, setErrView] = useState("good");
  const settlements = [
    { type:"Equities (Stocks)", cycle:"T+1", note:"All North American stocks and ETFs" },
    { type:"ETFs", cycle:"T+1", note:"Same as equities" },
    { type:"Most Mutual Funds", cycle:"T+1", note:"Standard redemption settlement" },
    { type:"Government Bonds", cycle:"T+1", note:"Canadian and US government bonds" },
    { type:"Corporate Bonds", cycle:"T+1", note:"Corporate debentures and bonds" },
    { type:"Money Market Funds", cycle:"T+0", note:"Same-day settlement — ONLY option for cash needed immediately" },
  ];
  return (
    <Reveal delay={100}>
      <SectionLabel color={T.teal}>8.4 — Trade Execution & Settlement</SectionLabel>
      <Card style={{ background:"linear-gradient(135deg,#f0fdf4,#f0fdfa)", border:`1px solid ${T.teal.border}` }}>
        <p style={{ margin:0, fontSize:13, color:T.text, lineHeight:1.7 }}>
          <strong>Restaurant Analogy.</strong> KYC/Suitability = the Chef designing the perfect meal. Execution/Settlement = the Waiter
          taking the order to the kitchen and ensuring the correct food reaches the correct table and the bill is paid.
          Write down the wrong order, send it to the wrong kitchen, or drop the food — and the perfect suitability analysis is ruined.
        </p>
      </Card>
      <strong style={{ fontSize:13, color:T.text, display:"block", marginBottom:10 }}>⏱️ Settlement Cycles — Must Memorize</strong>
      <div style={{ overflowX:"auto", marginBottom:16 }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
          <thead>
            <tr style={{ background:T.teal.bg }}>
              {["Asset Type","Settlement Cycle","Notes"].map(h => (
                <th key={h} style={{ padding:"10px 12px", textAlign:"left", color:T.teal.text, fontWeight:700, borderBottom:`2px solid ${T.teal.border}` }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {settlements.map((r,i) => (
              <tr key={i} style={{ background:i%2===0?T.bg:T.card, borderBottom:`1px solid ${T.border}` }}>
                <td style={{ padding:"9px 12px", fontWeight:600, color:T.text }}>{r.type}</td>
                <td style={{ padding:"9px 12px" }}>
                  <Tag color={r.cycle==="T+0" ? T.green : T.blue}>{r.cycle}</Tag>
                </td>
                <td style={{ padding:"9px 12px", color:T.muted }}>{r.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Card style={{ border:`1px solid ${T.red.border}`, background:T.red.bg, marginBottom:12 }}>
        <strong style={{ color:T.red.text, fontSize:13 }}>🏠 Critical Settlement Trap: The Condo Closing Scenario</strong>
        <p style={{ margin:"6px 0 0", fontSize:12, color:T.text, lineHeight:1.7 }}>
          Client calls Monday: "I need $50,000 CASH in my bank account TOMORROW to close on my condo."
          RR sells $50k of S&P 500 ETF (equity) on Monday.
          <strong> RESULT: Settlement is T+1 → cash arrives in BROKERAGE account Tuesday, available to WITHDRAW Wednesday. Condo deal fails.</strong>
          <br/>Compliant solution: Only a Money Market Fund (T+0) or cash already in the account works for same-day needs.
        </p>
      </Card>
      <strong style={{ fontSize:13, color:T.text, display:"block", marginBottom:8 }}>⚠️ Cash Account Rules</strong>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:16 }}>
        <Card style={{ border:`1px solid ${T.red.border}` }}>
          <strong style={{ fontSize:12, color:T.red.text }}>🚫 Freeriding Violation</strong>
          <div style={{ marginTop:8 }}>
            {["Mon 10am: Client has $0 cash — buys $10k of Stock A",
              "Mon 2pm: Stock A rises 10% — client sells for $11k BEFORE paying",
              "VIOLATION: Paid for Monday's trade with Monday afternoon's proceeds",
              "Requires Margin Account. Prohibited in Cash Accounts."].map((t,i) => (
              <div key={i} style={{ fontSize:11, color:T.text, padding:"4px 0", borderBottom:`1px solid ${T.border}`, lineHeight:1.5 }}>{i+1}. {t}</div>
            ))}
          </div>
        </Card>
        <Card style={{ border:`1px solid ${T.amber.border}` }}>
          <strong style={{ fontSize:12, color:T.amber.text }}>🔒 Overdue Cash Account Restriction</strong>
          <div style={{ marginTop:8 }}>
            {["Mon: Client buys $20k of TechCo with $0 cash",
              "Tue T+1: Settlement due — account still at $0",
              "Wed: Account is 'overdue' — firm MUST 'sell out' position",
              "PENALTY: Account restricted 90 days — must have 100% cash BEFORE placing any new trade"].map((t,i) => (
              <div key={i} style={{ fontSize:11, color:T.text, padding:"4px 0", borderBottom:`1px solid ${T.border}`, lineHeight:1.5 }}>{i+1}. {t}</div>
            ))}
          </div>
        </Card>
      </div>
      <strong style={{ fontSize:13, color:T.text, display:"block", marginBottom:8 }}>🔧 Order Error Handling</strong>
      <div style={{ display:"flex", gap:8, marginBottom:12 }}>
        {[["good","✅ The Good RR"],["bad","❌ The Bad RR"]].map(([v,l]) => (
          <button key={v} onClick={() => setErrView(v)}
            style={{ padding:"8px 18px", borderRadius:20, border:`1.5px solid ${errView===v ? (v==="good" ? T.green.text : T.red.text) : T.border}`,
              background:errView===v ? (v==="good" ? T.green.bg : T.red.bg) : T.card, cursor:"pointer",
              fontSize:12, fontWeight:700, color:errView===v ? (v==="good" ? T.green.text : T.red.text) : T.muted }}>
            {l}
          </button>
        ))}
      </div>
      {errView === "good" ? (
        <Card style={{ border:`1px solid ${T.green.border}`, background:T.green.bg }}>
          <strong style={{ color:T.green.text, fontSize:13 }}>Compliant Error Response: Wrong Stock Example</strong>
          <div style={{ marginTop:10 }}>
            {["10:00 AM: Client orders 100 TD Bank. RR accidentally buys 100 Teck Resources (TECK) at $45.",
              "10:05 AM: RR notices error on trade blotter. Immediately calls Branch Manager.",
              "Firm transfers 100 TECK to firm's 'Error Account' (out of client's account).",
              "Firm buys correct 100 TD Bank for client at current market price ($90.10).",
              "Client is charged $90.00 (original TD price). Firm eats the $0.10 difference + commissions.",
              "Client is made 100% whole and is never bothered. Firm and RR handle all consequences."].map((t,i) => (
              <div key={i} style={{ display:"flex", gap:8, padding:"6px 0", borderBottom:`1px solid ${T.green.border}` }}>
                <span style={{ fontSize:11, fontWeight:700, color:T.green.text, flexShrink:0 }}>{i+1}</span>
                <span style={{ fontSize:12, color:T.text, lineHeight:1.5 }}>{t}</span>
              </div>
            ))}
          </div>
        </Card>
      ) : (
        <Card style={{ border:`1px solid ${T.red.border}`, background:T.red.bg }}>
          <strong style={{ color:T.red.text, fontSize:13 }}>Prohibited Practices on Errors</strong>
          <div style={{ marginTop:10 }}>
            {[["Hiding", "Wait to see if Teck goes up by 4pm so no one notices → Fraud"],
              ["Gambling", "'I'll leave it and hope the error stock goes up so I come out ahead' → Fraud"],
              ["Pressuring", "'I actually bought you Teck — it's a great copper company! You're okay with that?' → Pressuring client to accept your error → Major violation"],
              ["Fixing yourself", "Placing a second order to 'undo' without involving Compliance → Unauthorized"],
            ].map(([k,v],i) => (
              <div key={i} style={{ padding:"8px 10px", borderRadius:6, background:T.card, marginBottom:6, border:`1px solid ${T.border}` }}>
                <span style={{ fontSize:12, fontWeight:700, color:T.red.text }}>✗ {k}: </span>
                <span style={{ fontSize:12, color:T.muted }}>{v}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </Reveal>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   SECTION 5 – MARGIN ACCOUNTS (LONG & SHORT)
══════════════════════════════════════════════════════════════════════════════ */
function Section5() {
  const [mode, setMode] = useState("long");
  const [longInputs, setLongInputs] = useState({ cash: 20000, marginReq: 50, priceChange: 50, maintReq: 30 });
  const [shortInputs, setShortInputs] = useState({ shares: 1000, price: 20, margin: 50, newPrice: 25 });

  const buyingPower = longInputs.cash / (longInputs.marginReq / 100);
  const firmLoan = buyingPower - longInputs.cash;
  const newValue = buyingPower * (1 + longInputs.priceChange / 100);
  const newEquity = newValue - firmLoan;
  const longReturn = ((newEquity - longInputs.cash) / longInputs.cash) * 100;
  const maintNeeded = newValue * (longInputs.maintReq / 100);
  const marginCallAmt = Math.max(0, maintNeeded - newEquity);

  const shortPosition = shortInputs.shares * shortInputs.price;
  const shortProceeds = shortPosition;
  const shortMarginRequired = shortPosition * (shortInputs.margin / 100);
  const shortTotalCollateral = shortProceeds + shortMarginRequired;
  const newShortPosition = shortInputs.shares * shortInputs.newPrice;
  const newRequiredCollateral = newShortPosition * 1.5;
  const shortMarginCall = Math.max(0, newRequiredCollateral - shortTotalCollateral);

  return (
    <Reveal delay={120}>
      <SectionLabel color={T.orange}>8.5 — Margin Accounts</SectionLabel>
      <Card style={{ background:T.red.bg, border:`1px solid ${T.red.border}` }}>
        <strong style={{ color:T.red.text, fontSize:13 }}>⚠️ Extreme Suitability Warning</strong>
        <p style={{ margin:"6px 0 0", fontSize:12, color:T.text, lineHeight:1.7 }}>
          A Margin Account is ONLY suitable for <strong>sophisticated, High Risk or Speculative clients</strong> with high risk capacity who fully understand leverage.
          Client must sign a separate <strong>Margin Agreement</strong> — a legal contract pledging all securities as collateral
          and giving the firm the right to <strong>force-liquidate any asset WITHOUT the client's permission</strong> if a margin call isn't met.
          <br/><strong>REGISTERED ACCOUNTS (TFSA, RRSP, RESP etc.) — NO MARGIN OR SHORT SELLING EVER. Income Tax Act prohibition. Absolute.</strong>
        </p>
      </Card>
      <div style={{ display:"flex", gap:8, marginBottom:16 }}>
        {[["long","📈 Long Margin (Leverage)"],["short","📉 Short Margin (Collateral)"]].map(([v,l]) => (
          <button key={v} onClick={() => setMode(v)}
            style={{ padding:"8px 20px", borderRadius:20, border:`1.5px solid ${mode===v ? T.blue.text : T.border}`,
              background:mode===v ? T.blue.bg : T.card, cursor:"pointer",
              fontSize:12, fontWeight:700, color:mode===v ? T.blue.text : T.muted }}>
            {l}
          </button>
        ))}
      </div>
      {mode === "long" ? (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
          <Card>
            <strong style={{ fontSize:13, color:T.text }}>🧮 Long Margin Calculator</strong>
            <div style={{ marginTop:12, display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
              {[["Client Cash ($)", "cash"],["Margin Req (%)", "marginReq"],["Price Change (%)", "priceChange"],["Maint. Req (%)", "maintReq"]].map(([label,key]) => (
                <div key={key}>
                  <div style={{ fontSize:11, color:T.muted, marginBottom:3 }}>{label}</div>
                  <input type="number" value={longInputs[key]}
                    onChange={e => setLongInputs(p => ({...p, [key]: Number(e.target.value)}))}
                    style={{ width:"100%", padding:"6px 8px", borderRadius:6, border:`1px solid ${T.border}`,
                      fontSize:12, background:T.bg, color:T.text, boxSizing:"border-box" }} />
                </div>
              ))}
            </div>
          </Card>
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            <Card style={{ background:T.blue.bg, border:`1px solid ${T.blue.border}` }}>
              <strong style={{ fontSize:12, color:T.blue.text }}>Purchase Power</strong>
              {[["Buying Power", `$${buyingPower.toLocaleString()}`],["Client Cash", `$${longInputs.cash.toLocaleString()}`],["Firm Loan", `$${firmLoan.toLocaleString()}`]].map(([k,v]) => (
                <div key={k} style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginTop:4 }}>
                  <span style={{ color:T.muted }}>{k}</span><strong style={{ color:T.text }}>{v}</strong>
                </div>
              ))}
            </Card>
            <Card style={{ background:longReturn >= 0 ? T.green.bg : T.red.bg,
              border:`1px solid ${longReturn >= 0 ? T.green.border : T.red.border}` }}>
              <strong style={{ fontSize:12, color:longReturn >= 0 ? T.green.text : T.red.text }}>Outcome After {longInputs.priceChange > 0 ? "+" : ""}{longInputs.priceChange}% Move</strong>
              {[["New Stock Value", `$${newValue.toLocaleString(undefined,{maximumFractionDigits:0})}`],
                ["Minus Firm Loan", `-$${firmLoan.toLocaleString()}`],
                ["Your Equity", `$${newEquity.toLocaleString(undefined,{maximumFractionDigits:0})}`],
                ["Personal Return", `${longReturn.toFixed(1)}% (vs ${longInputs.priceChange}% stock move)`],
                ...(marginCallAmt > 0 ? [["⚠️ Margin Call", `$${marginCallAmt.toLocaleString(undefined,{maximumFractionDigits:0})} required`]] : [])
              ].map(([k,v]) => (
                <div key={k} style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginTop:4 }}>
                  <span style={{ color:T.muted }}>{k}</span><strong style={{ color:T.text }}>{v}</strong>
                </div>
              ))}
            </Card>
          </div>
        </div>
      ) : (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
          <Card>
            <strong style={{ fontSize:13, color:T.text }}>🧮 Short Sale 150% Rule Calculator</strong>
            <div style={{ marginTop:12, display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
              {[["Shares Shorted","shares"],["Short Price ($)","price"],["New Price ($)","newPrice"]].map(([label,key]) => (
                <div key={key}>
                  <div style={{ fontSize:11, color:T.muted, marginBottom:3 }}>{label}</div>
                  <input type="number" value={shortInputs[key]}
                    onChange={e => setShortInputs(p => ({...p, [key]: Number(e.target.value)}))}
                    style={{ width:"100%", padding:"6px 8px", borderRadius:6, border:`1px solid ${T.border}`,
                      fontSize:12, background:T.bg, color:T.text, boxSizing:"border-box" }} />
                </div>
              ))}
            </div>
          </Card>
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            <Card style={{ background:T.purple.bg, border:`1px solid ${T.purple.border}` }}>
              <strong style={{ fontSize:12, color:T.purple.text }}>Initial Short Requirement (150%)</strong>
              {[["Short Position Value", `$${shortPosition.toLocaleString()}`],
                ["+ Proceeds (100%)", `+$${shortProceeds.toLocaleString()}`],
                ["+ Client Margin (50%)", `+$${shortMarginRequired.toLocaleString()}`],
                ["= Total Collateral (150%)", `$${shortTotalCollateral.toLocaleString()}`],
              ].map(([k,v]) => (
                <div key={k} style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginTop:4 }}>
                  <span style={{ color:T.muted }}>{k}</span><strong style={{ color:T.text }}>{v}</strong>
                </div>
              ))}
            </Card>
            <Card style={{ background:shortMarginCall > 0 ? T.red.bg : T.green.bg,
              border:`1px solid ${shortMarginCall > 0 ? T.red.border : T.green.border}` }}>
              <strong style={{ fontSize:12, color:shortMarginCall > 0 ? T.red.text : T.green.text }}>
                If Price Moves to ${shortInputs.newPrice}
              </strong>
              {[["New Position Value", `$${newShortPosition.toLocaleString()}`],
                ["New 150% Requirement", `$${newRequiredCollateral.toLocaleString()}`],
                ["Current Collateral", `$${shortTotalCollateral.toLocaleString()}`],
                ...(shortMarginCall > 0 ? [["⚠️ Margin Call", `$${shortMarginCall.toLocaleString(undefined,{maximumFractionDigits:0})} required`]] : [["✓ No Margin Call", "Collateral is sufficient"]])
              ].map(([k,v]) => (
                <div key={k} style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginTop:4 }}>
                  <span style={{ color:T.muted }}>{k}</span><strong style={{ color:T.text }}>{v}</strong>
                </div>
              ))}
            </Card>
          </div>
        </div>
      )}
      <div style={{ marginTop:12, display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(220px,1fr))", gap:10 }}>
        {[
          { icon:"⛔", label:"Registered Accounts", text:"RRSP/TFSA/RESP = Cash Accounts ONLY. No margin, no short selling. Income Tax Act absolute prohibition.", color:T.red },
          { icon:"🏗️", label:"Concentration Risk", text:"Firm can impose 70-100% margin requirement for undiversified single-stock positions, overriding the standard 50% rule.", color:T.amber },
          { icon:"💲", label:"Penny Stocks", text:"Speculative stocks not on 'Approved Margin List' = 100% margin requirement. No leverage allowed for high-risk/junk assets.", color:T.orange },
          { icon:"💸", label:"Owed Dividends (Short)", text:"Short-seller OWES dividends to the lender of the stock. Negative cash flow that adds to potential loss.", color:T.purple },
        ].map((r,i) => (
          <div key={i} style={{ padding:"12px 14px", borderRadius:10, border:`1.5px solid ${r.color.border}`, background:r.color.bg }}>
            <div style={{ fontWeight:700, fontSize:12, color:T.text, marginBottom:4 }}>{r.icon} {r.label}</div>
            <div style={{ fontSize:11, color:T.muted, lineHeight:1.5 }}>{r.text}</div>
          </div>
        ))}
      </div>
    </Reveal>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   SECTION 6 – REPORTING REQUIREMENTS
══════════════════════════════════════════════════════════════════════════════ */
function Section6() {
  const [doc, setDoc] = useState("confirm");
  const confirmFields = [
    { field:"Client Name & Account #", note:"Identifies who the trade belongs to" },
    { field:"Trade Date (T)", note:"Day the order was executed" },
    { field:"Settlement Date (T+1)", note:"Day money is legally due — critical for client's cash management" },
    { field:"Security Name & Symbol", note:"e.g., Royal Bank of Canada (RY.TO)" },
    { field:"Action: BUY / SELL / SHORT SALE", note:"What direction the trade was" },
    { field:"Quantity", note:"e.g., 100 shares" },
    { field:"Price Per Share", note:"e.g., $140.10" },
    { field:"Principal Amount", note:"Total before commission (100 × $140.10 = $14,010)" },
    { field:"Commission ⭐", note:"MANDATORY explicit line-item. Must be separate and visible." },
    { field:"Net Amount", note:"Total debited/credited ($14,010 + $100 = $14,110)" },
    { field:"Capacity: Agent or Principal ⭐", note:"AGENT = firm brokered the trade. PRINCIPAL = firm sold from its OWN inventory (conflict of interest — must disclose)" },
  ];
  const statementFields = [
    { field:"Statement Period", note:"e.g., March 1–31, 2024" },
    { field:"Account Details", note:"Client name, account #, RR name and contact info" },
    { field:"Activity Summary (Chequebook)", note:"Every trade, dividend, fee, deposit, withdrawal in chronological order" },
    { field:"Holdings Summary (Inventory)", note:"Every security owned at month end" },
    { field:"Quantity & Security", note:"What you hold and how much" },
    { field:"Adjusted Cost Base (ACB) ⭐", note:"What you PAID — needed for capital gain calculation" },
    { field:"Market Value", note:"What it's worth NOW" },
    { field:"Unrealized Gain/Loss", note:"Paper profit/loss (not yet taxable)" },
    { field:"Opening & Closing Balances", note:"Grand total at start and end of period" },
  ];
  return (
    <Reveal delay={140}>
      <SectionLabel color={T.green}>8.6 — Reporting Requirements to Clients</SectionLabel>
      <Card style={{ background:T.green.bg, border:`1px solid ${T.green.border}` }}>
        <strong style={{ color:T.green.text, fontSize:13 }}>3-Step Restaurant Analogy</strong>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginTop:10 }}>
          {[
            ["1️⃣ Verbal Confirmation", "Waiter reads order back before sending to kitchen: 'That's 1 steak medium-rare + $5 mushroom sauce. Confirm?'"],
            ["2️⃣ Trade Confirmation", "Itemized receipt that arrives at the table: '1 Steak @ $40, 1 Sauce @ $5'. Sent by T+1."],
            ["3️⃣ Account Statement", "Monthly credit card statement showing ALL meals for the month in one summary. Monthly if activity, quarterly minimum."],
          ].map(([t,d],i) => (
            <div key={i} style={{ background:T.card, borderRadius:8, padding:"10px 12px" }}>
              <div style={{ fontWeight:700, fontSize:12, color:T.green.text, marginBottom:4 }}>{t}</div>
              <div style={{ fontSize:11, color:T.muted, lineHeight:1.5 }}>{d}</div>
            </div>
          ))}
        </div>
      </Card>
      <Card style={{ border:`1px solid ${T.blue.border}`, background:T.blue.bg, marginBottom:12 }}>
        <strong style={{ color:T.blue.text, fontSize:13 }}>💬 Verbal Confirmation — The Mandatory Pre-Trade Checklist (CFRs)</strong>
        <p style={{ margin:"8px 0 0", fontSize:12, color:T.text, lineHeight:1.7, fontStyle:"italic" }}>
          "Okay, just to confirm: we are <strong>BUYING</strong> 100 shares of <strong>RBC</strong> at the <strong>Market Price</strong> in your <strong>TFSA (#123)</strong>.
          The approximate cost is $14,000, and the <strong>commission for this trade will be $100</strong>. Do you <strong>confirm</strong> this instruction?"
        </p>
        <p style={{ margin:"8px 0 0", fontSize:11, color:T.muted }}>
          Under CFRs: Must disclose ALL costs verbally BEFORE placing. Client cannot give 'informed consent' without knowing the fee.
          If you skip this and the client sees $100 commission on the confirmation → complaint.
        </p>
      </Card>
      <div style={{ display:"flex", gap:8, marginBottom:12 }}>
        {[["confirm","Trade Confirmation"],["statement","Account Statement"]].map(([v,l]) => (
          <button key={v} onClick={() => setDoc(v)}
            style={{ padding:"8px 18px", borderRadius:20, border:`1.5px solid ${doc===v ? T.blue.text : T.border}`,
              background:doc===v ? T.blue.bg : T.card, cursor:"pointer",
              fontSize:12, fontWeight:700, color:doc===v ? T.blue.text : T.muted }}>
            {l}
          </button>
        ))}
      </div>
      {doc === "confirm" ? (
        <div>
          <div style={{ display:"flex", gap:12, marginBottom:10 }}>
            <Tag color={T.teal}>Who sends it: Investment Dealer (the Firm)</Tag>
            <Tag color={T.blue}>When: By end of T+1 (next business day)</Tag>
            <Tag color={T.green}>What: One specific trade</Tag>
          </div>
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
              <thead>
                <tr style={{ background:T.blue.bg }}>
                  {["Required Field","Why It Matters"].map(h => (
                    <th key={h} style={{ padding:"9px 12px", textAlign:"left", color:T.blue.text, fontWeight:700, borderBottom:`2px solid ${T.blue.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {confirmFields.map((r,i) => (
                  <tr key={i} style={{ background:i%2===0?T.bg:T.card, borderBottom:`1px solid ${T.border}` }}>
                    <td style={{ padding:"9px 12px", fontWeight:600, color: r.field.includes("⭐") ? T.amber.text : T.text }}>{r.field}</td>
                    <td style={{ padding:"9px 12px", color:T.muted }}>{r.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div>
          <div style={{ display:"flex", gap:12, marginBottom:10 }}>
            <Tag color={T.teal}>Who sends it: Investment Dealer (the Firm)</Tag>
            <Tag color={T.green}>When: Monthly if ANY activity. Quarterly minimum always.</Tag>
          </div>
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
              <thead>
                <tr style={{ background:T.green.bg }}>
                  {["Required Field","Why It Matters"].map(h => (
                    <th key={h} style={{ padding:"9px 12px", textAlign:"left", color:T.green.text, fontWeight:700, borderBottom:`2px solid ${T.green.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {statementFields.map((r,i) => (
                  <tr key={i} style={{ background:i%2===0?T.bg:T.card, borderBottom:`1px solid ${T.border}` }}>
                    <td style={{ padding:"9px 12px", fontWeight:600, color: r.field.includes("⭐") ? T.amber.text : T.text }}>{r.field}</td>
                    <td style={{ padding:"9px 12px", color:T.muted }}>{r.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Card style={{ marginTop:12, border:`1px solid ${T.purple.border}`, background:T.purple.bg }}>
            <strong style={{ color:T.purple.text, fontSize:13 }}>🛡️ Gatekeeper Case Study: Monthly Review Catches Elder Abuse</strong>
            <p style={{ margin:"6px 0 0", fontSize:12, color:T.text, lineHeight:1.7 }}>
              An RR proactively reviews account statements before mailing. He notices his 85-yr-old Low Risk client Mrs. Jones has:
              Mar 5: Sell $10k BMO Bond Fund → Mar 6: Wire Transfer $10k to unknown account →
              Mar 12: Sell $10k Fortis → Mar 13: Wire Transfer $10k.
              The RR never recommended these trades. This is a major red flag for Financial Crime/Elder Abuse.
              He immediately calls Branch Manager and Compliance → firm freezes account and contacts Trusted Contact Person (TCP) → discovers new caregiver was coercing client.
              <strong> The account statement review fulfilled the Gatekeeper duty and protected the client.</strong>
            </p>
          </Card>
        </div>
      )}
    </Reveal>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   FLASHCARDS
══════════════════════════════════════════════════════════════════════════════ */
const FLASHCARDS = [
  { q:"What is 'Best Execution'?", a:"Getting the best TOTAL OUTCOME for the client — not just lowest commission or fastest execution. Factors: Price, Speed, Certainty, Total Cost. Contextual: liquid stock = price + speed. Illiquid = price over speed." },
  { q:"What is Wash Trading?", a:"Simultaneously buying in one account and selling in another account you control — at the same price. Creates fake volume with NO change in beneficial ownership. Intent: lure in other investors." },
  { q:"What is Spoofing (Layering)?", a:"Placing a massive fake order (a 'spoof') you NEVER intend to fill — to scare the market into moving, then cancelling the fake order and profiting. Prohibited: order was not 'bona fide'." },
  { q:"What is 'Marking the Close'?", a:"A form of Artificial Pricing where a trade is placed at 3:59 PM NOT as a genuine investment, but to spike the closing price for personal gain (e.g., inflating a fund manager's year-end performance bonus)." },
  { q:"What is Front Running?", a:"Trading in your own/spouse's/controlled account using PRIVATE knowledge of a client's pending order. Violates the Client Priority Rule: client orders ALWAYS come before RR personal trades." },
  { q:"What is the Gatekeeper duty?", a:"The RR's proactive obligation to maintain an 'alert, curious attitude' — identify, inquire, and escalate suspicious transactions. 'I was just following orders' is NOT a defense." },
  { q:"What triggers a Suspicious Transaction escalation?", a:"Any trade inconsistent with the client's KYC: financials, knowledge, risk profile, past activity, or basic logic. KYC is your #1 compliance tool." },
  { q:"What is the 'Tipping Off' prohibition?", a:"Once an STR (Suspicious Transaction Report) is filed with FINTRAC, it is a CRIMINAL OFFENSE to tell the client (or hint) that a report was filed. Protects the investigation." },
  { q:"Market Order — key risk?", a:"ZERO price control. 'Slippage' in volatile/illiquid markets — fills at the next available Ask (buy) or Bid (sell), which can be far worse than expected. Never use for illiquid penny stocks." },
  { q:"Limit Order — key risk?", a:"NO guarantee of execution. Price may never reach your limit. You get price control but risk missing the trade entirely." },
  { q:"Stop-Loss Order — the critical trap?", a:"NOT a Limit Order. It's a TRIGGER that becomes a Market Order when stop price is touched. In a flash crash: stop touches $95 → becomes Market Order → fills at $75 → stock snaps back to $99. Guarantees selling at worst price." },
  { q:"What is IOC vs FOK?", a:"IOC (Immediate or Cancel): Fill whatever is available RIGHT NOW, cancel the rest. Allows partial fills. FOK (Fill or Kill): Fill the ENTIRE order RIGHT NOW or cancel everything. No partial fills." },
  { q:"What is an Iceberg Order?", a:"Institutional tool: shows only a small 'display size' (e.g., 1,000 of 1,000,000 shares) to prevent panicking the market. As each tranche fills, the next one appears automatically. Critical Best Execution tool for large blocks." },
  { q:"What is the T+1 Settlement Rule?", a:"All North American equities, ETFs, mutual funds, and bonds settle T+1 (trade date + 1 business day). Only Money Market Funds settle T+0 (same day). Key exam trap: 'sell stock Monday, cash available to withdraw Wednesday.'" },
  { q:"What is Freeriding?", a:"Selling a stock to pay for a buy order in a Cash Account — using proceeds that don't exist yet. 'Paid' for Monday's buy with Monday afternoon's sell proceeds. Requires a Margin Account for this activity." },
  { q:"What is the Long Margin 50% Rule?", a:"Client must put up minimum 50% of purchase value as margin. Firm lends the other 50%. $20k cash → $40k buying power. Gains AND losses are magnified: stock +50% → personal return +100%. Stock -50% → personal return -100%." },
  { q:"What is the Short Margin 150% Rule?", a:"To short $20k of stock: Proceeds (100%) = $20k + Client Margin (50%) = $10k → Total frozen collateral = $30k (150%). If stock rises, new 150% of new position value is required → triggers margin call." },
  { q:"What is Re-Hypothecation?", a:"The client's right granted in the Margin Agreement allowing the firm to use the client's pledged securities for its own purposes (e.g., as collateral for the firm's own loans, or to lend out to short-sellers)." },
  { q:"When must a Trade Confirmation be sent?", a:"By end of T+1 (next business day after the trade). Sent by the FIRM (not the RR). Must include: trade date, settlement date, security, quantity, price, commission (explicit line item), and capacity (Agent or Principal)." },
  { q:"Agent vs. Principal capacity on Trade Confirmation?", a:"AGENT: Firm brokered the trade — found shares from a third party in the market. PRINCIPAL: Firm sold from its OWN inventory — firm was the counterparty. Principal is a CONFLICT OF INTEREST and MUST be disclosed." },
];

function Flashcards() {
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [mastered, setMastered] = useState(new Set());
  const remaining = FLASHCARDS.filter((_,i) => !mastered.has(i));
  function next(master) {
    if (master) setMastered(s => new Set([...s, idx]));
    const available = FLASHCARDS.map((_,i) => i).filter(i => !mastered.has(i) && (master ? i !== idx : true));
    const nextIdx = available.length === 0 ? 0 : available[Math.floor(Math.random() * available.length)];
    if (available.length === 0) setMastered(new Set());
    setIdx(nextIdx); setFlipped(false);
  }
  if (remaining.length === 0) return (
    <Card style={{ textAlign:"center", padding:32 }}>
      <div style={{ fontSize:40, marginBottom:8 }}>🏆</div>
      <div style={{ fontFamily:"Georgia,serif", fontSize:18, color:T.text, marginBottom:8 }}>All 20 cards mastered!</div>
      <button onClick={() => { setMastered(new Set()); setIdx(0); setFlipped(false); }}
        style={{ padding:"10px 24px", background:T.blue.text, color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700 }}>Restart</button>
    </Card>
  );
  const card = FLASHCARDS[idx];
  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
        <Tag color={T.blue}>{remaining.length} remaining</Tag>
        <Tag color={T.green}>{mastered.size} mastered</Tag>
      </div>
      <div onClick={() => setFlipped(!flipped)} style={{ cursor:"pointer" }}>
        <div style={{ minHeight:140, borderRadius:12, border:`2px solid ${flipped ? T.green.border : T.blue.border}`,
          background:flipped ? T.green.bg : T.blue.bg, padding:"24px 28px", display:"flex", alignItems:"center",
          justifyContent:"center", transition:"all 0.3s", position:"relative" }}>
          <div style={{ position:"absolute", top:12, right:14, fontSize:11, color:T.muted, fontWeight:600 }}>
            {flipped ? "ANSWER" : "QUESTION — tap to flip"}
          </div>
          <p style={{ margin:0, fontSize:14, lineHeight:1.7, textAlign:"center", color:T.text,
            fontFamily:flipped?"system-ui":"Georgia,serif", fontWeight:flipped?400:600 }}>
            {flipped ? card.a : card.q}
          </p>
        </div>
      </div>
      {flipped && (
        <div style={{ display:"flex", gap:10, marginTop:12 }}>
          <button onClick={() => next(false)} style={{ flex:1, padding:10, borderRadius:8, border:`1.5px solid ${T.red.border}`,
            background:T.red.bg, color:T.red.text, cursor:"pointer", fontWeight:700, fontSize:13 }}>✗ Still Learning</button>
          <button onClick={() => next(true)} style={{ flex:1, padding:10, borderRadius:8, border:`1.5px solid ${T.green.border}`,
            background:T.green.bg, color:T.green.text, cursor:"pointer", fontWeight:700, fontSize:13 }}>✓ Got It</button>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   QUIZ
══════════════════════════════════════════════════════════════════════════════ */
const QUESTIONS = [
  { q:"A client wants to sell 100,000 shares of SmallCap Co. It has 5,000 shares average daily volume and a $5.00 bid / $5.50 ask spread. The RR places ONE market sell order at 9:30 AM. The client's average fill price is $4.15. What did the RR violate?",
    opts:["Wash Trading — the RR traded against himself","Best Execution — the RR should have 'worked the order' in small limit-order blocks over several days, prioritizing PRICE over SPEED","Front Running — the RR traded ahead of the client","Freeriding — the client had no cash in the account"],
    ans:1, explain:"Best Execution for illiquid stocks means PRICE over SPEED. Dumping 100k shares into a 5k daily volume market crashes the client's own price. The RR must use limit orders and 'work the order' over days." },
  { q:"An RR hears that a huge mutual fund client just placed a 5-million-share buy order for Suncor. Before the order finishes executing, the RR buys 1,000 Suncor shares in his personal account and sells them minutes later for a $1,000 profit. This is:",
    opts:["Wash Trading — the RR created fake volume","Spoofing — the RR placed a fake order","Front Running — the RR used private knowledge of a client order to profit personally","Best Execution — the RR was finding the best price"],
    ans:2, explain:"Front Running = using material, non-public information about a CLIENT'S ORDER to profit personally. This violates the Client Priority Rule. Client orders ALWAYS come before RR personal trades. Career-ending offense." },
  { q:"A portfolio manager buys a massive block of his top holding at 3:59 PM on December 31 to spike the year-end closing price and inflate his bonus. This is:",
    opts:["Best Execution — he was getting the best price","Wash Trading — he traded with himself","Artificial Pricing ('Marking the Close') — his INTENT was to manipulate the price, not make a genuine investment","Front Running — he used non-public information"],
    ans:2, explain:"'Marking the Close' is Artificial Pricing. The trade's intent was NOT a good investment — it was to MANIPULATE the closing price for personal gain (bonus inflation). The intent is what makes it a violation." },
  { q:"A 'Low Risk' retiree client owns 1,000 shares of StableCo at $100. An RR places a Stop-Loss order at $95 to 'protect' the client. A flash crash hits — price touches $95, the order becomes a Market Order, fills at $75, then stock snaps back to $99. The primary suitability failure is:",
    opts:["The RR used a Limit Order instead of a Market Order","The Stop-Loss became a Market Order upon touching $95, guaranteeing the client sold at the WORST price during a temporary crash — unsuitable for a Low Risk client","The stock was illiquid so Best Execution was violated","The client needed a FOK order instead"],
    ans:1, explain:"Critical trap: A Stop-Loss is NOT a Limit Order. It's a TRIGGER that launches a Market Order. For Low Risk clients, this can guarantee the worst possible exit. The RR should have used a Sell Limit Order if the client needed price protection." },
  { q:"A 'Speculative' client deposits $30,000 cash into a Margin Account. The standard margin requirement is 50%. What is the maximum total value of blue-chip stock this client can purchase?",
    opts:["$30,000 (same as cash)","$45,000 (50% more)","$60,000 (client's $30k + firm's $30k loan)","$15,000 (firm keeps half as collateral)"],
    ans:2, explain:"Long Margin: client's $30k = 50% of purchase. Firm lends the other $30k (50%). Total buying power = $60,000. If stock rises 50% → $90k value - $30k loan = $60k equity → personal return = +100%. Leverage magnifies both gains and losses." },
  { q:"A client wants to short 500 shares of TechCo at $40/share. The CIRO 150% rule applies. What is the TOTAL collateral that must be frozen in the account?",
    opts:["$10,000 (50% margin only)","$20,000 (100% proceeds only)","$30,000 (100% proceeds + 50% client margin = 150% of $20,000 position)","$40,000 (200% for speculative positions)"],
    ans:2, explain:"150% Rule: Short position = $40 × 500 = $20,000. Frozen collateral = 100% (proceeds from sale = $20,000) + 50% (client's own margin = $10,000) = $30,000 total. If stock price rises, new 150% of new value triggers a margin call." },
  { q:"A client buys 100 shares of RBC on Tuesday, May 10th. By what date must the firm send the Trade Confirmation, and what settlement date will be shown on it?",
    opts:["Sent by end of May 31 (month-end). Settlement: May 15","Sent by end of Wednesday May 11 (T+1). Settlement: Wednesday May 11 (T+1)","Sent by end of Thursday May 12 (T+2). Settlement: Thursday May 12","Sent by the RR verbally on the same day. No written confirmation needed"],
    ans:1, explain:"Trade Confirmation must be sent by END OF T+1 (next business day). Settlement date shown will also be T+1 for equities. This is the firm's (not the RR's) mandatory legal obligation." },
];

function Quiz() {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [current, setCurrent] = useState(0);
  const score = Object.entries(answers).filter(([i,a]) => QUESTIONS[+i].ans === a).length;
  function selectAnswer(qi, ai) { if (!submitted) setAnswers(p => ({...p, [qi]: ai})); }
  if (submitted) return (
    <div>
      <div style={{ display:"flex", alignItems:"center", gap:24, marginBottom:20, padding:"20px 24px",
        background:T.card, borderRadius:12, border:`1px solid ${T.border}` }}>
        <ScoreRing score={score} total={QUESTIONS.length} color={score >= 6 ? T.green : score >= 4 ? T.amber : T.red} />
        <div>
          <div style={{ fontFamily:"Georgia,serif", fontSize:20, color:T.text, fontWeight:700 }}>{score}/{QUESTIONS.length} Correct</div>
          <div style={{ fontSize:13, color:T.muted, marginTop:4 }}>
            {score >= 6 ? "Excellent — ready for the RSE exam!" : score >= 4 ? "Good — review flagged areas" : "Focus on UMIR violations and margin rules"}
          </div>
          <button onClick={() => { setAnswers({}); setSubmitted(false); setCurrent(0); }}
            style={{ marginTop:10, padding:"6px 16px", background:T.blue.text, color:"#fff", border:"none",
              borderRadius:6, cursor:"pointer", fontSize:12, fontWeight:700 }}>Retake</button>
        </div>
      </div>
      {QUESTIONS.map((q,i) => {
        const ua = answers[i]; const correct = ua === q.ans;
        return (
          <div key={i} style={{ marginBottom:12, padding:"14px 16px", borderRadius:10,
            background:correct ? T.green.bg : T.red.bg, border:`1.5px solid ${correct ? T.green.border : T.red.border}` }}>
            <div style={{ display:"flex", gap:8, alignItems:"flex-start" }}>
              <span style={{ fontSize:16 }}>{correct ? "✓" : "✗"}</span>
              <div style={{ flex:1 }}>
                <p style={{ margin:"0 0 6px", fontSize:13, fontWeight:700, color:T.text }}>{q.q}</p>
                {!correct && ua !== undefined && <p style={{ margin:"0 0 4px", fontSize:12, color:T.red.text }}>Your answer: {q.opts[ua]}</p>}
                <p style={{ margin:"0 0 4px", fontSize:12, color:T.green.text, fontWeight:600 }}>Correct: {q.opts[q.ans]}</p>
                <p style={{ margin:0, fontSize:12, color:T.muted, fontStyle:"italic" }}>{q.explain}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
  const q = QUESTIONS[current];
  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
        <Tag color={T.blue}>Q {current+1} of {QUESTIONS.length}</Tag>
        <span style={{ fontSize:12, color:T.muted }}>{Object.keys(answers).length} answered</span>
      </div>
      <div style={{ background:T.blue.bg, border:`1px solid ${T.blue.border}`, borderRadius:10, padding:"16px 18px", marginBottom:14 }}>
        <p style={{ margin:0, fontSize:14, lineHeight:1.7, color:T.text, fontFamily:"Georgia,serif" }}>{q.q}</p>
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:16 }}>
        {q.opts.map((opt,ai) => {
          const selected = answers[current] === ai;
          return (
            <button key={ai} onClick={() => selectAnswer(current, ai)}
              style={{ padding:"12px 16px", borderRadius:8, border:`1.5px solid ${selected ? T.blue.text : T.border}`,
                background:selected ? T.blue.bg : T.card, cursor:"pointer", textAlign:"left",
                fontSize:13, color:T.text, transition:"all 0.15s", lineHeight:1.5 }}>
              <span style={{ fontWeight:700, color:T.blue.text, marginRight:6 }}>{["A","B","C","D"][ai]}.</span>{opt}
            </button>
          );
        })}
      </div>
      <div style={{ display:"flex", gap:10 }}>
        {current > 0 && <button onClick={() => setCurrent(c=>c-1)} style={{ padding:"9px 18px", borderRadius:8,
          border:`1px solid ${T.border}`, background:T.card, cursor:"pointer", fontSize:13, color:T.muted }}>← Prev</button>}
        {current < QUESTIONS.length-1
          ? <button onClick={() => setCurrent(c=>c+1)} style={{ padding:"9px 22px", borderRadius:8,
              background:T.blue.text, color:"#fff", border:"none", cursor:"pointer", fontSize:13, fontWeight:700 }}>Next →</button>
          : <button onClick={() => setSubmitted(true)} disabled={Object.keys(answers).length < QUESTIONS.length}
              style={{ padding:"9px 22px", borderRadius:8,
                background:Object.keys(answers).length < QUESTIONS.length ? T.dim : T.green.text,
                color:"#fff", border:"none", cursor:Object.keys(answers).length < QUESTIONS.length ? "not-allowed" : "pointer",
                fontSize:13, fontWeight:700 }}>Submit Quiz ✓</button>}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   QUICK REFERENCE TABLE
══════════════════════════════════════════════════════════════════════════════ */
const QUICK_REF = [
  { topic:"Best Execution Factors", detail:"Price (most important) | Speed | Certainty of fill | Total cost (commission + spread)" },
  { topic:"Illiquid Stock Best Execution", detail:"Prioritize PRICE over SPEED — use limit orders in small blocks ('work the order') over days" },
  { topic:"Wash Trading", detail:"Trading with yourself/controlled account at same price to create fake volume. No change in ownership." },
  { topic:"Spoofing", detail:"Fake order placed with no intent to fill — cancels after market moves in desired direction" },
  { topic:"Marking the Close", detail:"Trade at 3:59 PM to manipulate closing price for personal gain. Classic: PM inflating year-end bonus." },
  { topic:"Front Running", detail:"Trading personal/spouse/controlled account using private knowledge of client's pending order. Criminal." },
  { topic:"Red Flag Categories", detail:"Inconsistent with: Financials | Knowledge | Risk Profile | Past Activity | Basic Logic" },
  { topic:"Fire Drill: Suspicious Trade", detail:"Recognize → Inquire → PAUSE trade → Escalate to Manager/Compliance → Document" },
  { topic:"Tipping Off", detail:"ILLEGAL to tell client an STR was filed with FINTRAC. Criminal offense. Protects investigation." },
  { topic:"OSC Whistleblower", detail:"Up to $5M award | Anonymity | Anti-reprisal protection (illegal to fire for reporting)" },
  { topic:"Market Order", detail:"Fills at best available Ask (buy) or Bid (sell). Speed + certainty. Zero price control. Illiquid = disaster." },
  { topic:"Limit Order", detail:"Price control guaranteed. No execution guarantee. Set below market for buy, above for sell." },
  { topic:"Stop-Loss", detail:"NOT a Limit Order. Trigger → Market Order when stop price touched. Flash crash = catastrophic fill." },
  { topic:"IOC vs FOK", detail:"IOC = partial fills OK, cancel rest. FOK = 100% or nothing, right now." },
  { topic:"Settlement Cycles", detail:"T+1: Equities, ETFs, Bonds, Most MFs | T+0: Money Market Funds only" },
  { topic:"Long Margin 50% Rule", detail:"Client puts up 50%, firm lends 50%. $20k cash → $40k buying power. Gains AND losses magnified 2×." },
  { topic:"Short Margin 150% Rule", detail:"Total frozen = 100% (proceeds) + 50% (client margin) = 150% of short position value" },
  { topic:"Margin + Registered Accounts", detail:"ABSOLUTELY PROHIBITED by Income Tax Act. TFSA/RRSP/RESP = Cash Accounts only. No exceptions." },
  { topic:"Trade Confirmation", detail:"Sent by Firm by T+1. Must include: commission (explicit), settlement date, capacity (Agent or Principal)" },
  { topic:"Account Statement Frequency", detail:"Monthly if ANY activity (trade, dividend, fee). Quarterly MINIMUM even if no activity." },
];

function QuickRef() {
  return (
    <div style={{ overflowX:"auto" }}>
      <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
        <thead>
          <tr style={{ background:T.blue.bg }}>
            {["Topic","Key Detail"].map(h => (
              <th key={h} style={{ padding:"10px 12px", textAlign:"left", color:T.blue.text, fontWeight:700, borderBottom:`2px solid ${T.blue.border}` }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {QUICK_REF.map((r,i) => (
            <tr key={i} style={{ background:i%2===0?T.bg:T.card, borderBottom:`1px solid ${T.border}` }}>
              <td style={{ padding:"9px 12px", fontWeight:700, color:T.text, whiteSpace:"nowrap" }}>{r.topic}</td>
              <td style={{ padding:"9px 12px", color:T.muted, lineHeight:1.6 }}>{r.detail}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   MAIN APP
══════════════════════════════════════════════════════════════════════════════ */
const TABS = [
  { id:"s1", label:"UMIR & Abusive Trading", icon:"⚖️" },
  { id:"s2", label:"Gatekeeping", icon:"🚪" },
  { id:"s3", label:"Order Types", icon:"📋" },
  { id:"s4", label:"Settlement", icon:"⏱️" },
  { id:"s5", label:"Margin Accounts", icon:"📈" },
  { id:"s6", label:"Reporting", icon:"📄" },
  { id:"flash", label:"Flashcards", icon:"🃏" },
  { id:"quiz", label:"Quiz", icon:"✏️" },
  { id:"ref", label:"Quick Ref", icon:"📋" },
];

export default function App() {
  const [active, setActive] = useState("s1");
  const [scrollPct, setScrollPct] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      setScrollPct((el.scrollTop / (el.scrollHeight - el.clientHeight)) * 100);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div style={{ fontFamily:"system-ui,-apple-system,sans-serif", background:T.bg, minHeight:"100vh", color:T.text }}>
      <div style={{ position:"fixed", top:0, left:0, right:0, height:3, background:T.border, zIndex:1000 }}>
        <div style={{ height:"100%", background:`linear-gradient(90deg,${T.blue.text},${T.red.text})`,
          width:`${scrollPct}%`, transition:"width 0.1s" }} />
      </div>
      <div style={{ background:T.card, borderBottom:`1px solid ${T.border}`, padding:"20px 24px 0", position:"sticky", top:3, zIndex:100 }}>
        <div style={{ maxWidth:960, margin:"0 auto" }}>
          <div style={{ display:"flex", alignItems:"baseline", gap:12, marginBottom:14 }}>
            <span style={{ fontFamily:"Georgia,serif", fontSize:22, fontWeight:700, color:T.text }}>Element 8</span>
            <span style={{ color:T.muted, fontSize:14 }}>Execution & Market Integrity</span>
            <Tag color={T.blue}>7 Exam Questions</Tag>
          </div>
          <div style={{ display:"flex", gap:2, overflowX:"auto", paddingBottom:0 }}>
            {TABS.map(t => (
              <button key={t.id} onClick={() => { setActive(t.id); window.scrollTo({ top:0, behavior:"smooth" }); }}
                style={{ padding:"8px 12px", border:"none", borderBottom:`3px solid ${active===t.id ? T.blue.text : "transparent"}`,
                  background:"none", cursor:"pointer", fontSize:12, fontWeight:active===t.id?700:500,
                  color:active===t.id ? T.blue.text : T.muted, whiteSpace:"nowrap", transition:"all 0.2s",
                  display:"flex", alignItems:"center", gap:5 }}>
                <span>{t.icon}</span><span>{t.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
      <div style={{ maxWidth:960, margin:"0 auto", padding:"28px 20px 60px" }}>
        {active === "s1" && <Section1 />}
        {active === "s2" && <Section2 />}
        {active === "s3" && <Section3 />}
        {active === "s4" && <Section4 />}
        {active === "s5" && <Section5 />}
        {active === "s6" && <Section6 />}
        {active === "flash" && <Reveal><SectionLabel color={T.teal}>Flashcards — 20 Cards</SectionLabel><Flashcards /></Reveal>}
        {active === "quiz" && <Reveal><SectionLabel color={T.orange}>Practice Quiz — 7 Questions</SectionLabel><Quiz /></Reveal>}
        {active === "ref" && <Reveal><SectionLabel color={T.blue}>Quick Reference — 20 Key Facts</SectionLabel><QuickRef /></Reveal>}
      </div>
    </div>
  );
}
